import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useToggler } from '@cbhq/cds-common';
import { Button } from '@cbhq/cds-web/buttons';
import { InputIconButton, TextInput } from '@cbhq/cds-web/controls';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { TextBody, TextTitle1, TextTitle3 } from '@cbhq/cds-web/typography';
import { flushQueue } from '@cbhq/client-analytics';

import { logFundsAccessCriticalStep } from ':fat/analytics/logMetrics';
import { SecurityPhraseFAQ } from ':fat/components/FAQ/SecurityPhraseFAQ';
import { TwoColumnLayout } from ':fat/components/PageLayout/TwoColumnLayout';
import { SignerContext } from ':fat/components/SecurityFrameSignerContext';
import { Signer } from ':fat/core/signer';
import { deriveAllXpubs } from ':fat/core/xpubs';
import { enteredSeedPhrase, goToLandingPage } from ':fat/messaging/entities/mainPage/actions';
import { messages } from ':fat/pages/security-frame/SeedPhraseEntry/SeedPhraseEntryPageMessages';
import { AppRoutes } from ':fat/routing/routes';

const SECURITY_SETTINGS_URL = 'https://beta.commerce.coinbase.com/settings/security';

export function SeedPhraseEntryPage() {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { isPhone } = useBreakpoints();

  const [isVisible, { toggle: toggleIsVisible }] = useToggler();
  const [type, setType] = useState('text');
  const [seedPhrase, setSeedPhrase] = useState('');

  const { setSigner } = useContext(SignerContext);

  useEffect(() => {
    setType(isVisible ? 'text' : 'password');
  }, [isVisible]);

  const handleRecoveryPress = useCallback(() => {
    window.open(SECURITY_SETTINGS_URL, '_blank', 'noopener noreferrer');
  }, []);

  const trimmedPhrase = useMemo(() => {
    return seedPhrase
      .trim()
      .split(/\s+/)
      .map((w) => w.trim())
      .join(' ');
  }, [seedPhrase]);

  const isSeedPhraseValid = useMemo(() => {
    const words = trimmedPhrase.split(/\s+/);
    return words.length === 12;
  }, [trimmedPhrase]);

  const handleSeedPhraseChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newPhrase = event.target.value;
      setSeedPhrase(newPhrase);
    },
    [setSeedPhrase],
  );

  const handleBackPress = useCallback(() => {
    void goToLandingPage();
  }, []);

  const handleNextPress = useCallback(() => {
    if (!isSeedPhraseValid) {
      return;
    }

    logFundsAccessCriticalStep({ step: 'entered_seed_phrase' });

    void (async () => {
      const xpubs = deriveAllXpubs(trimmedPhrase);
      try {
        setSigner(new Signer(trimmedPhrase));
        await enteredSeedPhrase(xpubs);
        flushQueue().finally(() => navigate(AppRoutes.SECURITY_FRAME.THINKING));
      } catch (err) {
        // TODO surface error in a better way
        // eslint-disable-next-line no-console
        console.error(err);
      }
    })();
  }, [isSeedPhraseValid, trimmedPhrase, setSigner, navigate]);

  return (
    <TwoColumnLayout
      primaryContent={
        <VStack gap={2}>
          <TextTitle1 as="h1">{formatMessage(messages.seedPhraseEntryTitle)}</TextTitle1>
          <TextBody color="foregroundMuted" as="p">
            {formatMessage(messages.seedPhraseEntryDescription)}
          </TextBody>
          <HStack
            bordered
            spacing={3}
            gap={3}
            borderRadius="rounded"
            flexWrap={isPhone ? 'wrap' : 'nowrap'}
            justifyContent="space-between"
          >
            <VStack gap={1}>
              <TextTitle3 as="p">{formatMessage(messages.recoveryPhraseTitle)}</TextTitle3>
              <TextBody color="foregroundMuted" as="p">
                {formatMessage(messages.recoveryPhraseDescription)}
              </TextBody>
            </VStack>
            <HStack alignItems="center">
              <Button
                onPress={handleRecoveryPress}
                variant="secondary"
                endIcon="externalLink"
                compact
              >
                {formatMessage(messages.googleDriveRecoveryButton)}
              </Button>
            </HStack>
          </HStack>
          <TextInput
            label="Seed recovery phrase"
            type={type}
            helperText={
              isSeedPhraseValid
                ? 'Looks good! That is a valid secret recovery prhase.'
                : 'This is a 12-word phrase.'
            }
            placeholder="Enter secret phrase"
            onChange={handleSeedPhraseChange}
            value={seedPhrase}
            variant={isSeedPhraseValid ? 'positive' : 'foregroundMuted'}
            end={
              <InputIconButton
                name={isVisible ? 'visibleActive' : 'visibleInactive'}
                onPress={toggleIsVisible}
                accessibilityLabel={isVisible ? 'Hide seed phrase' : 'Show seed phrase'}
              />
            }
          />

          <HStack gap={1} justifyContent="flex-end">
            <Button variant="secondary" onPress={handleBackPress}>
              Back
            </Button>
            <Button
              endIcon="forwardArrow"
              variant="primary"
              onPress={handleNextPress}
              disabled={!isSeedPhraseValid}
            >
              Next
            </Button>
          </HStack>
        </VStack>
      }
      secondaryContent={<SecurityPhraseFAQ />}
    />
  );
}
